 
import React from 'react';
import capitalize from 'lodash/capitalize';
import { BreadcrumbsItem } from '@cp/ds/src/components/breadcrumbs/model';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { coreRouterApi, useClientWebRouteParams, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { coreCareerApi } from '@cp/entities/career';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { useCurrentCity } from '@cp/entities/location';
import { useBaseBreadcrumbs } from 'src/features/breadcrumbs';

export const useCatalogBreadcrumbs = (): BreadcrumbsItem[] => {
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const { routeId, pageProps, hasCitySeparatedContent } = useClientWebRouteParams<ClientWebPage.Catalog>();
  const { careerId, catalogType, filter } = pageProps;
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const { data: routeMeta } = coreRouterApi.useGetClientWebRouteMetaQuery({ routeId: Number(routeId) }, { skip: !routeId });
  const generateAppPath = usePathGenerator();
  const breadcrumbs = useBaseBreadcrumbs();

  return React.useMemo(() => {
    const careerDictItem = careerDictionary?.[careerId];

    if (!careerDictItem) {
      return breadcrumbs;
    }

    return [
      ...breadcrumbs,
      ...(filter
        ? [
            {
              path: generateAppPath({ page: ClientWebPage.Catalog, pageProps: { careerId, catalogType } }),
              label:
                hasCitySeparatedContent && currentCity
                  ? catalogType === CatalogItemType.Resume
                    ? `${capitalize(careerDictItem.translation.namePlural)} в ${currentCity.translation.namePrepositional}`
                    : `Работа для ${careerDictItem.translation.namePluralGenitive} в ${currentCity.translation.namePrepositional}`
                  : catalogType === CatalogItemType.Resume
                    ? capitalize(careerDictItem.translation.namePlural)
                    : `Работа для ${careerDictItem.translation.namePluralGenitive}`,
            },
          ]
        : []),
      {
        path: generateAppPath({ page: ClientWebPage.Catalog, pageProps: { careerId, catalogType, filter } }),
        label:
          hasCitySeparatedContent && currentCity && !filter
            ? catalogType === CatalogItemType.Resume
              ? `${capitalize(routeMeta?.breadcrumb ?? careerDictItem.translation.namePlural)} в ${currentCity.translation.namePrepositional}`
              : `${routeMeta?.breadcrumb ? capitalize(routeMeta?.breadcrumb) : `Работа для ${careerDictItem.translation.namePluralGenitive}`} в ${currentCity.translation.namePrepositional}`
            : routeMeta?.breadcrumb
              ? capitalize(routeMeta?.breadcrumb)
              : catalogType === CatalogItemType.Resume
                ? capitalize(careerDictItem.translation.namePlural)
                : `Работа для ${careerDictItem.translation.namePluralGenitive}`,
      },
    ];
  }, [
    breadcrumbs,
    careerDictionary,
    careerId,
    catalogType,
    currentCity,
    filter,
    generateAppPath,
    hasCitySeparatedContent,
    routeMeta?.breadcrumb,
  ]);
};
